/// <amd-module name="Traceability/Common/TraceabilityBaseCard" />
import React = require("react");
import { ITraceabilityCardData, ITraceabilityCardProps, IDataResult } from "Traceability/Common/Contracts";
import * as Resources from "Traceability/Common/Resources";
import { Utilities } from "Traceability/Common/Utilities";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Card, ICardTitleProps } from "VSSUI/Card";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as StringUtils from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
export abstract class TraceabilityBaseCard<T> extends VssComponent<ITraceabilityCardProps<T>, {}> {

    protected abstract renderTable(context: IVssComponentContext, displayCount: number, cardTitleProps: ICardTitleProps): JSX.Element;

    public constructor(props: ITraceabilityCardProps<T>, context: IVssComponentContext) {
        super(props, context);
        this._cardData = {
            artifactDetails: props && props.artifactDetails || {},
            data: props && props.data || [],
            innerErrorMessage: props && props.innerErrorMessage || "",
            displayCount: new ObservableValue<number>(this._initialDisplayCount),
            isLoading: new ObservableValue<boolean>(false)
        } as ITraceabilityCardData<T>;

        // get data only if error is not present and data length is zero
        const dataLength = this._cardData.data!.length; // data null check done already
        if (!this._cardData.innerErrorMessage && dataLength === 0) {
            // repository should contain at least name or id, then only call for data
            // exception for pipeline type. We don't need them in that case
            if ((props.getPipelineTypeWorkItems && this._cardData.artifactDetails.artifactVersion?.type == "Pipeline") ||
                (this._cardData.artifactDetails.repository && (this._cardData.artifactDetails.repository.id || this._cardData.artifactDetails.repository.name))) {
                this._cardData.isLoading.value = true;
                props.getCardData(this._cardData.artifactDetails).then((result: IDataResult<T>) => {
                    // Update the changes/workItems data.
                    this._cardData.data = result && result.data || [];
                    this._cardData.innerErrorMessage = result && result.innerErrorMessage || "";
                    this._cardData.isLoading.value = false;
                }).catch(reason => {
                    this._cardData.data = [];
                    this._cardData.innerErrorMessage = reason;
                    this._cardData.isLoading.value = false;
                });
            }
        }
    }

    public render(): JSX.Element {
        const commandBarItems = this._getCommandBarItems();
        return (
            <Observer isLoading={this._cardData.isLoading} displayCount={this._cardData.displayCount}>
                {
                    (props: { isLoading: boolean; displayCount: number; }) => {
                        const cardDataLength = this._cardData.data!.length; // data null check done already
                        const displayShowMoreButton = !props.isLoading && (cardDataLength > props.displayCount);
                        // either content is being fetched or we have content already
                        const hasContent = props.isLoading || cardDataLength > 0;
                        // hide title only if we have content to show, otherwise do not hide the title
                        const hideCardTitle = this.props.hideCardTitle && hasContent;
                        // no padding when we have content
                        const contentProps = { contentPadding: !hasContent, className: hasContent ? "justify-center" : "" };
                        const titleProps: ICardTitleProps = hideCardTitle ? {} : { text: Utilities.getArtifactCardHeaderText(this._cardData.artifactDetails) };
                        const cardClassName = `traceability-card ${hasContent ? "bolt-card-no-vertical-padding" : ""}`;
                        const headerClassName = hideCardTitle ? "no-v-padding" : hasContent ? "traceability-card-title" : "";

                        return (<Card
                            className={cardClassName}
                            contentProps={contentProps}
                            titleProps={titleProps}
                            headerClassName={headerClassName}
                            headerCommandBarItems={commandBarItems}
                        >
                            <div className="flex-column">
                                {/* show spinner when data being fetched */}
                                {props.isLoading ?
                                    <Spinner
                                        className="padding-16"
                                        size={SpinnerSize.medium}
                                        label={Resources.LoadingText}
                                    />
                                    : this.renderTable(
                                        this.context,
                                        props.displayCount,
                                        titleProps)
                                }
                                {displayShowMoreButton && (
                                    <div className="traceability-show-more-button">
                                        <Button
                                            text={Resources.ShowMoreButtonText}
                                            onClick={this._onShowMoreClick}
                                        />
                                    </div>
                                )}
                            </div>
                        </Card>);
                    }
                }
            </Observer>
        );
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] | IObservableArray<IHeaderCommandBarItem> {
        const uri = this.artifactDetailsUri;
        if (this.props.hideCardTitle || !uri) {
            return [];
        }

        const cardIndex = this.props.cardIndex != undefined ? this.props.cardIndex.toString() : StringUtils.newGuid();
        const commandId = `viewArtifactDetails-${cardIndex}`;
        if (typeof (uri) == "string") {
            return [{
                important: true,
                id: commandId,
                text: Resources.ViewDetailsButtonText,
                href: uri,
                onActivate: this._onViewDetailsButtonClick
            }];
        }
        else {
            let commandBarItems = new ObservableArray([] as IHeaderCommandBarItem[]);
            uri.then(artifactDetailsUri => {
                if (!!artifactDetailsUri) {
                    commandBarItems.push({
                        important: true,
                        id: commandId,
                        text: Resources.ViewDetailsButtonText,
                        href: artifactDetailsUri,
                        onActivate: this._onViewDetailsButtonClick
                    });
                }
            });
            return commandBarItems;
        }
    }

    private _onViewDetailsButtonClick = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const uri = this.artifactDetailsUri;
        if( !uri ) return;
        event && event.persist();
        if ( typeof(uri) == "string" ) {
            onClickFPS(this.context.pageContext, uri, true, event);
        }
        else {
            uri.then(artifactUri => {
                onClickFPS(this.context.pageContext, artifactUri, true, event);
            });
        }
    }

    private get artifactDetailsUri(): string | Promise<string> {
        return this._cardData.artifactDetails
            && this._cardData.artifactDetails.artifactVersion
            && this._cardData.artifactDetails.artifactVersion.displayUri || "";
    }

    private _onShowMoreClick = () => {
        this._cardData.displayCount.value = this._cardData.data!.length; // data null check done already
    }

    protected _cardData: ITraceabilityCardData<T>;
    private readonly _initialDisplayCount = 5;
}